import {useMemo} from 'react';

import {useKeyInfoQueryContext} from '../../components/MultiRunWorkspace/KeyInfoQueryContext';
import {useMaybeWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {type RunHistoryKeyInfo} from '../../types/run';
import {useIsNgwEnabled} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {
  usePanelGenHistoryKeysError,
  usePanelGenHistoryKeysIsLoading,
  useSampledHistoryKeys,
} from './panelGenHistoryKeysStore';

export type ActiveHistoryKeyInfo = {
  historyKeyInfo: RunHistoryKeyInfo;
  loading: boolean;
  error: Error | undefined;
};

/**
 * The history keys currently in force: the sampled panel-gen set under
 * next-gen workspaces, and the full history-keys query otherwise. Callers that
 * only need the keys read `historyKeyInfo` and ignore the rest.
 *
 * The branch is on the ramp flag rather than on whether key info came back
 * empty. Key info is also empty when the query ran and found nothing — no runs
 * selected, or a rejected run filter — and substituting the sampled keys there
 * would report keys for a selection that has none.
 *
 * `shouldSkipFullHistoryKeysQuery` is what actually gates the full query, and
 * it skips that query for every NGW-enabled project, so the flag alone gives
 * the same answer here.
 *
 * Callers that must not act on the legacy query's keys before eligibility
 * resolves should gate on `useIsCurrentProjectNgwEligible().loading`
 * themselves — that check needs the router, so it stays out of here.
 *
 * The sampled keys also need somewhere to come from. Only the workspace mounts
 * the sync that fills that store, and its query reads the workspace refs
 * context, so on a surface without one — a report, whose route still carries
 * the entity and project a ramp check reads — the store stays empty forever.
 *
 * Those surfaces keep the full query, which still has keys for them: reports
 * mount `KeyInfoQueryContextProvider` with `allowMultiRunsetHistoryKeys`, and
 * that query never consults NGW, so nothing skips it. Without this check an
 * NGW-eligible report reads the empty store instead and its media panels
 * resolve no card type.
 */
export function useActiveHistoryKeyInfo(): ActiveHistoryKeyInfo {
  const {keyInfoQuery} = useKeyInfoQueryContext();
  const sampledHistoryKeys = useSampledHistoryKeys();
  const sampledHistoryKeysLoading = usePanelGenHistoryKeysIsLoading();
  const sampledHistoryKeysError = usePanelGenHistoryKeysError();
  const isNgwEnabled = useIsNgwEnabled();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const usesSampledKeys = isNgwEnabled && workspaceRefs != null;

  return useMemo(() => {
    if (usesSampledKeys) {
      return {
        historyKeyInfo: sampledHistoryKeys,
        loading: sampledHistoryKeysLoading,
        error: sampledHistoryKeysError,
      };
    }
    // `refreshing` is deliberately not folded into `loading`. While the query
    // refetches (every run-visibility toggle changes its variables) it keeps
    // the previous key set, and when the response carries the same hash it
    // returns that same object. The panel-bank auto-sync effect keys off
    // `loading` and `historyKeyInfo`, so surfacing the refetch here made it
    // regenerate every panel on each toggle even when no key changed. A
    // response with a new hash still produces a new `historyKeyInfo` identity
    // and still regenerates. The refresh loader reads `refreshing` directly.
    return {
      historyKeyInfo: keyInfoQuery.historyKeyInfo,
      loading: keyInfoQuery.loading,
      error: keyInfoQuery.error ?? undefined,
    };
  }, [
    usesSampledKeys,
    sampledHistoryKeys,
    sampledHistoryKeysLoading,
    sampledHistoryKeysError,
    keyInfoQuery,
  ]);
}
